import React, {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import styled, {css} from 'styled-components';
import axios from 'axios';
import {useTranslation} from 'react-i18next';
import Moment from 'moment';
import 'moment/dist/locale/pt-br';
import 'moment/dist/locale/es';
import ScrollContainer from 'react-indiana-drag-scroll';
import {Helmet} from 'react-helmet';
import {useHistory, useRouteMatch} from 'react-router';

import {getCurrentLang} from '../utils/i18n';
import {
    PageBodyWrapper,
    Flex,
    useIsMobile,
    mobileMediaQuery,
    notMobileMediaQuery,
    tabletMediaQuery,
} from '../styles';
import ProgrammationItem, {
    ProgrammingContent,
} from '../components/ProgrammationItem';
import Loader from '../components/Loader';
import Button from '../components/Button';

import CNitoTile from '../assets/cnito_tile_2.png';
import {ReactComponent as ChevronIcon} from '../assets/arrow.svg';
import SizzleVideoES from '../assets/videos/cnito-sizzle-es.mp4';
import SizzleVideoPT from '../assets/videos/cnito-sizzle-pt.mp4';
import Sheet from 'react-modal-sheet';
import {debugCNitoUrl} from '../helpers';

Moment.locale(getCurrentLang());

const Cartoonito = props => {
    const user = useSelector(state => state.user);
    const idp = user && user.idp;
    const {t} = useTranslation();
    const isMobile = useIsMobile();
    const bodyElement = document.querySelector('body');
    const scrollContainerRef = React.useRef();
    const {path} = useRouteMatch();
    const history = useHistory();

    const [whatsOn, setWhatsOn] = useState(
        path === '/programacao' || path === '/programacion',
    );
    const [programmation, setProgrammation] = useState(null);
    const [currentDay, setCurrentDay] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [showDetails, setShowDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        loadProgrammation();

        window.addEventListener('focus', () => {
            !isFocused && setIsFocused(true);
        });
        window.addEventListener('click', () => {
            !isFocused && setIsFocused(true);
        });

        window.turner_metadata.trackAction.push({
            type: 'pageview',
            data: {
                pageName: `cnplus.com/cartoonito/`,
                section: 'cartoonito',
                subsection: 'nvs',
                contenttype: 'Cartoonito',
                contenttitle: 'Cartoonito',
                presentationtemplate: 'desktop',
                platform: 'web',
                domain: 'cnplus.com',
                region: 'latam',
                country:
                    user && user.countryIsoCode && user.countryName
                        ? `${user.countryIsoCode.toLowerCase()}|${
                              user.countryName
                          }`
                        : 'not loaded',
                brand: 'cartoon network',
                pageviewevent: 1,
                authstate:
                    user && user.jwtToken ? 'authenticated' : 'unauthenticated',
                mvpd: idp || 'idp not loaded',
            },
        });

        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        setWhatsOn(path === '/programacao' || path === '/programacion');

        // eslint-disable-next-line
    }, [path]);

    useEffect(() => {
        if (isMobile && whatsOn) {
            let top = window.scrollY;

            bodyElement.style.position = 'fixed';
            bodyElement.style.top = `-${top}px`;
            bodyElement.style.left = '0px';
            bodyElement.style.right = '0px';
        } else {
            const scrollY = bodyElement.style.top;

            bodyElement.style.position = '';
            bodyElement.style.top = '';
            bodyElement.style.left = '';
            bodyElement.style.right = '';

            window.scrollTo(0, -parseInt(scrollY || '0'));
        }

        return () => {
            const scrollY = bodyElement.style.top;

            bodyElement.style.position = '';
            bodyElement.style.top = '';
            bodyElement.style.left = '';
            bodyElement.style.right = '';

            window.scrollTo(0, -parseInt(scrollY || '0'));
        };

        // eslint-disable-next-line
    }, [isMobile, whatsOn]);

    useEffect(() => {
        loadProgrammation();

        if (scrollContainerRef.current) {
            let slideSize = isMobile ? 134 : 182;

            let scrollPos =
                slideSize * (currentDay + 1) -
                scrollContainerRef.current.clientWidth / 2 +
                slideSize / 2;

            scrollContainerRef.current.scrollTo({
                left: scrollPos,
                behavior: 'smooth',
            });
        }

        // eslint-disable-next-line
    }, [currentDay]);

    const loadProgrammation = async () => {
        setLoading(true);

        const from = Moment()
            .add(currentDay, 'days')
            .startOf('day')
            .format();
        const to = Moment()
            .add(currentDay, 'days')
            .endOf('day')
            .format();
        try {
            let scheduleCatalog = 'BOOMLA_PAN';

            if (window.location.host.includes('.mx')) {
                scheduleCatalog = 'BOOMLA_MX';
            } else if (
                window.location.host.includes('.br') ||
                getCurrentLang() === 'pt'
            ) {
                scheduleCatalog = 'BOOMLA_BR';
            }

            let response = await axios.get(
                // eslint-disable-next-line
                `https://schedule.dmti.cloud/schedule?from=${from}&to=${to}&feed=${scheduleCatalog}&mapped=true&output=json&offset=-03:00`,
            );

            if (response.status === 200) {
                let feed =
                    response.data['com.turner.dmtla.schedule.dto.ScheduleDTO']
                        ?.feeds[0]['com.turner.dmtla.schedule.dto.FeedDTO']
                        ?.shows[0]['com.turner.dmtla.schedule.dto.ShowDTO'];

                setProgrammation(feed);
            }
        } catch (err) {
            // eslint-disable-next-line
            console.error(err);

            setProgrammation([]);
        } finally {
            setLoading(false);
        }
    };

    const handlePrevDay = () => {
        if (currentDay > -1) {
            setCurrentDay(currentDay - 1);
        }
    };

    const handleNextDay = () => {
        if (currentDay < 6) {
            setCurrentDay(currentDay + 1);
        }
    };

    const renderDays = () => {
        let days = [];

        for (let index = -1; index < 7; index++) {
            let dayLabel = Moment()
                .add(index, 'days')
                .format('DD MMM');

            if (index === -1) {
                dayLabel = t('cartoonito.programming.yesterday');
            } else if (index === 0) {
                dayLabel = t('cartoonito.programming.today');
            } else if (index === 1) {
                dayLabel = t('cartoonito.programming.tomorrow');
            }

            days = [
                ...days,
                <Day
                    key={dayLabel}
                    active={currentDay === index}
                    onClick={() => setCurrentDay(index)}
                >
                    {dayLabel}
                </Day>,
            ];
        }

        return days;
    };

    const handleCloseProgrammation = () => {
        //if (window.location.hostname.indexOf('cartoonito') >= 0) {
        history.push(`/${debugCNitoUrl()}`);
        //} else {
        //    history.push('/cartoonito');
        //}
    };

    const onItemClick = item => {
        setCurrentItem(item);
        setIsLoading(true);
        setShowModal(true);
    };

    const onDetailLoad = (item, details) => {
        //for some reason, this is not working correctly and I fail to see why.
        //currentItem is not updating correctly
        //if (currentItem && item.id === currentItem.id) {
        setShowDetails(details);
        setIsLoading(false);
        //}
    };

    useEffect(() => {
        if (!isMobile) {
            setShowModal(false);
        }
    }, [isMobile]);

    return (
        <Container>
            <Helmet>
                <title>Cartoonito</title>
                <meta name="description" content={t('home.metaDescription')} />
            </Helmet>

            <ContentColumn>
                {!whatsOn ? (
                    <>
                        <Title>{t('cartoonito.landing.title')}</Title>

                        <Text>{t('cartoonito.landing.message')}</Text>

                        <SizzleReel>
                            <video
                                width="100%"
                                height="100%"
                                autoPlay
                                controls
                                loop
                            >
                                <source
                                    src={
                                        getCurrentLang() === 'pt'
                                            ? SizzleVideoPT
                                            : SizzleVideoES
                                    }
                                    type="video/mp4"
                                />
                                Your browser does not support the video tag.
                            </video>
                        </SizzleReel>

                        <Button
                            margin="26px auto 0 auto"
                            onClick={() =>
                                history.push(
                                    getCurrentLang() === 'es'
                                        ? '/programacion'
                                        : '/programacao',
                                )
                            }
                        >
                            {t('cartoonito.landing.cta')}
                        </Button>
                    </>
                ) : (
                    <>
                        <ProgrammationCard>
                            <ProgrammationHeader>
                                {t('cartoonito.programming.title')}
                                {isMobile && (
                                    <CloseButton
                                        onClick={handleCloseProgrammation}
                                    >
                                        <ChevronIcon
                                            title={t('controls.close')}
                                        />
                                    </CloseButton>
                                )}
                            </ProgrammationHeader>

                            <DaySelectionContainer>
                                <ButtonPrevious onClick={handlePrevDay}>
                                    <ChevronIcon />
                                </ButtonPrevious>

                                <DaysScroll innerRef={scrollContainerRef}>
                                    {renderDays()}
                                </DaysScroll>

                                <ButtonNext onClick={handleNextDay}>
                                    <ChevronIcon />
                                </ButtonNext>
                            </DaySelectionContainer>

                            <ProgrammationContent>
                                {loading ? (
                                    <Loader />
                                ) : (
                                    programmation?.length > 0 &&
                                    programmation.map(
                                        item =>
                                            item && (
                                                <ProgrammationItem
                                                    key={item.id}
                                                    item={item}
                                                    onItemClick={onItemClick}
                                                    onDetailLoad={onDetailLoad}
                                                />
                                            ),
                                    )
                                )}
                            </ProgrammationContent>
                        </ProgrammationCard>
                    </>
                )}
            </ContentColumn>
            <CustomSheet
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                detent="content-height"
            >
                <CustomSheet.Container>
                    <CustomSheet.Content>
                        <ProgrammingContent
                            item={currentItem}
                            loading={isLoading}
                            showDetails={showDetails}
                            margin="32px 22px 22px 22px"
                            time
                        />
                    </CustomSheet.Content>
                </CustomSheet.Container>

                <CustomSheet.Backdrop onTap={() => setShowModal(false)} />
            </CustomSheet>
        </Container>
    );
};

const Container = styled(PageBodyWrapper)`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    min-height: calc(100vh - 392px);
    position: relative;
    max-width: 100%;

    background: url(${CNitoTile}), #0066ff;
    background-size: 100%;
    background-repeat: repeat;

    ${tabletMediaQuery} {
        padding-top: 80px;
    }
    ${mobileMediaQuery} {
        height: 100vh;
    }
`;

const ContentColumn = styled.div`
    width: auto;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    margin-top: 120px;
    margin-bottom: 78px;
    max-width: 90%;
    position: relative;

    ::after {
        position: absolute;
        top: 0;
        left: -32px;
        right: -32px;
        bottom: 0;
        content: '';
        display: block;
        background-color: #0000;
        z-index: -1;
        max-width: 100vw;
    }

    ${tabletMediaQuery} {
        margin-top: 40px;
    }
`;

const Title = styled.h1`
    color: rgb(236, 0, 140);
    font-family: 'AvenirNextRoundedStd';
    font-size: 28px;
    font-weight: bold;
    line-height: 38px;
    text-align: center;
    max-width: 672px;

    margin: 0 auto 8px auto;
`;

const Text = styled.p`
    max-width: 672px;
    color: rgb(0, 0, 0);
    font-size: 18px;
    font-family: 'AvenirNextRoundedStd';
    font-weight: bold;
    text-align: center;
    line-height: 24px;
    white-space: pre-line;

    margin: 0 auto 24px auto;
`;

const SizzleReel = styled.div`
    width: 535px;
    background: rgb(0, 0, 0);
    border-radius: 8px;
    overflow: hidden;
    aspect-ratio: 535 / 300;
    display: flex;
    flex-direction: column;
    max-width: 90%;

    iframe {
        flex: 1;
    }
`;

const ProgrammationCard = styled.div`
    border-radius: 34px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgb(255, 255, 255);
    box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.5);
    width: 672px;
    max-width: 100%;

    ${mobileMediaQuery} {
        position: fixed;
        top: 0px;
        left: 0px;
        right: 0px;
        bottom: 1px;
        z-index: 100;
        border-radius: 0;
    }
`;

const ProgrammationHeader = styled.div`
    background-color: #d82e8a;
    width: 100%;
    height: 62px;
    color: rgb(255, 255, 255);
    font-size: 20px;
    font-family: 'Rodger';
    font-weight: bold;
    text-transform: uppercase;
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;

    ${mobileMediaQuery} {
        height: 78px;
    }
`;

const DaySelectionContainer = styled(Flex)`
    flex-direction: row;
    width: 100%;
    height: 48px;
    background-color: #e9e1e9;
    padding: 12px 78px;
    box-sizing: border-box;
    flex-wrap: nowrap;

    ${mobileMediaQuery} {
        justify-content: space-between;
        padding: 12px 0px;
    }
`;

const ProgrammationContent = styled.div`
    padding: 24px 40px;
    width: 100%;
    box-sizing: border-box;
    flex: 1;
    overflow-y: auto;

    ${mobileMediaQuery} {
        padding: 24px 24px;
    }
`;

const ButtonPrevious = styled.button`
    margin-left: 0;
    margin-right: auto;
    width: 12px;
    height: 22px;
    padding: 0;
    border: none;
    background-color: transparent;
    cursor: pointer;

    svg {
        width: 12px;
        height: 22px;
        transform: scaleX(-1);
    }

    ${mobileMediaQuery} {
        display: none;
    }
`;

const ButtonNext = styled(ButtonPrevious)`
    margin-right: 0;
    margin-left: auto;

    svg {
        transform: scaleX(1);
    }
`;

const Day = styled.div`
    min-width: 70px;
    flex: 1;
    color: #4b4b4b;
    font-size: 18px;
    font-family: 'AvenirNextRoundedStd';
    font-weight: 600;
    text-align: center;
    line-height: 24px;
    margin: 0 56px;
    cursor: pointer;

    ${({active}) =>
        active &&
        css`
            color: #ec008c;
        `}

    ${mobileMediaQuery} {
        margin: 0 32px;
    }
`;

const CloseButton = styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 50%;
    right: 28px;
    margin: 0px;
    transform: translateY(-50%);

    height: 28px;
    width: 28px;
    box-sizing: content-box;

    border: none;
    background-color: transparent;
    padding: 0;
    cursor: pointer;

    svg {
        width: 28px;
        height: 28px;

        ${notMobileMediaQuery} {
            g {
                fill: #fff;
            }
        }
    }

    ${mobileMediaQuery} {
        right: initial;
        left: 28px;
        transform: translateY(-50%) scaleX(-1);

        svg path {
            fill: #fff;
        }
    }
`;

const DaysScroll = styled(ScrollContainer)`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    width: auto;
    box-sizing: border-box;
    overflow-x: scroll;
    width: 90%;
    margin: auto;

    ${mobileMediaQuery} {
        width: 100%;
    }
`;

const CustomSheet = styled(Sheet)`
    .react-modal-sheet-backdrop {
        background-color: rgba(0, 0, 0, 0.85) !important;
        border: none;
    }
    .react-modal-sheet-container {
        width: calc(100% - 32px) !important;
        margin: 0px 16px !important;
        border-top-left-radius: 34px !important;
        border-top-right-radius: 34px !important;
    }
    .react-modal-sheet-header {
        /* custom styles */
    }
    .react-modal-sheet-drag-indicator {
        /* custom styles */
    }
    .react-modal-sheet-content {
        /* custom styles */
    }
`;

export default Cartoonito;
